import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { of } from 'rxjs';
import { environment } from '@environments/environment';
import { ModalDemoModeService } from './modal-demo-mode.service';
import {
	DEMO_ACCOUNT,
	DEMO_COMPANY,
	DEMO_OFFICER,
	DEMO_OFFICER_OPTION,
	DEMO_TRUST,
	MODAL_DEMO_HTTP_BODY,
} from './modal-demo.dummy';

/**
 * When the modal demo page is active, never hit the real API — except auth/session
 * endpoints, which must stay real so we don't wipe the logged-in support user.
 * Must run after urlAdjusterInterceptor so URLs are absolute.
 */
export const modalDemoHttpInterceptor: HttpInterceptorFn = (req, next) => {
	const demoMode = inject(ModalDemoModeService);
	if (!demoMode.isEnabled()) {
		return next(req);
	}

	const isApi =
		req.url.startsWith(environment.apiUrl) || req.url.includes('/api/');
	if (!isApi || isSessionRequest(req.url)) {
		return next(req);
	}

	return of(
		new HttpResponse({
			status: 200,
			body: pickDemoBody(req.method, req.url),
		})
	);
};

function isSessionRequest(url: string): boolean {
	const lower = url.toLowerCase();
	return (
		lower.includes('/auth/') ||
		lower.includes('/user/profile') ||
		lower.includes('/user/edit')
	);
}

function pickDemoBody(method: string, url: string): unknown {
	const lower = url.toLowerCase();
	const isGet = method.toUpperCase() === 'GET';

	if (lower.includes('/trust/') && !lower.includes('/history') && !lower.includes('/trusts')) {
		return DEMO_TRUST;
	}
	// Exact company resource only — not /company/officers/123 etc.
	if (isGet && /\/company\/\d+\/?(\?|$)/.test(lower)) {
		return DEMO_COMPANY;
	}
	if (lower.includes('select-company-list') || lower.includes('portfolio/companies')) {
		return { data: [], recordsTotal: 0, recordsFiltered: 0, user_total_companies: 0 };
	}
	if (lower.includes('/account') && isGet && /\/account\/?(\?|$)/.test(lower)) {
		return DEMO_ACCOUNT;
	}
	if (lower.includes('namereservation')) {
		return [
			{
				id: 1,
				name: 'DEMO NAME ONE',
				tracking_no: 'TRACK-1',
			},
			{
				id: 2,
				name: 'DEMO NAME TWO',
				tracking_no: 'TRACK-2',
			},
		];
	}
	if (lower.includes('/pricing') || lower.includes('billing/type')) {
		return [
			{
				name: 'Demo service',
				filter: 'name-change',
				amount: '25',
				cipcFee: '50',
			},
		];
	}
	// List endpoints like /company/officers/:companyId must be arrays (id is company, not officer).
	if (
		lower.includes('/officers') ||
		lower.includes('/directors') ||
		lower.includes('/shareholders') ||
		lower.includes('/beneficiaries') ||
		lower.includes('/trustees')
	) {
		return [DEMO_OFFICER_OPTION, DEMO_OFFICER_OPTION];
	}
	if (lower.includes('officer') || lower.includes('director')) {
		return isGet && !/\/\d+(\?|$)/.test(url) ? [DEMO_OFFICER_OPTION] : DEMO_OFFICER;
	}
	if (lower.includes('/alert')) {
		return [];
	}

	const wantsCollection =
		isGet && !/\/\d+(\?|$)/.test(url) && !lower.includes('/ifexists');

	if (wantsCollection) {
		return [MODAL_DEMO_HTTP_BODY, MODAL_DEMO_HTTP_BODY];
	}

	return MODAL_DEMO_HTTP_BODY;
}
